/**
 * LINE LIFF Authentication
 */

import liff from '@line/liff';
import { setLineUserId, clearAuth } from '../client';
import type { LineProfile } from '../../types';

let isInitialized = false;

/**
 * Initialize LINE LIFF
 */
export async function initializeLiff(liffId: string): Promise<boolean> {
  if (isInitialized) {
    return true;
  }

  try {
    await liff.init({ liffId });
    isInitialized = true;

    // If logged in, store the user ID
    if (liff.isLoggedIn()) {
      const profile = await liff.getProfile();
      setLineUserId(profile.userId);
    }

    return true;
  } catch (error) {
    console.error('LIFF initialization failed:', error);
    return false;
  }
}

/**
 * Check if LIFF is initialized
 */
export function isLiffInitialized(): boolean {
  return isInitialized;
}

/**
 * Check if user is logged in via LINE
 */
export function isLineLoggedIn(): boolean {
  return isInitialized && liff.isLoggedIn();
}

/**
 * Login via LINE
 */
export function lineLogin(redirectUri?: string): void {
  if (!isInitialized) {
    console.error('LIFF not initialized');
    return;
  }

  liff.login({ redirectUri });
}

/**
 * Logout from LINE
 */
export function lineLogout(): void {
  if (!isInitialized) {
    return;
  }

  liff.logout();
  clearAuth();
}

/**
 * Get LINE profile
 */
export async function getLineProfile(): Promise<LineProfile | null> {
  if (!isInitialized || !liff.isLoggedIn()) {
    return null;
  }

  try {
    const profile = await liff.getProfile();
    return {
      userId: profile.userId,
      displayName: profile.displayName,
      pictureUrl: profile.pictureUrl,
      statusMessage: profile.statusMessage,
    };
  } catch (error) {
    console.error('Failed to get LINE profile:', error);
    return null;
  }
}

/**
 * Get LINE User ID (async, fetches from LIFF)
 */
export async function getLineUserIdFromLiff(): Promise<string | null> {
  if (!isInitialized || !liff.isLoggedIn()) {
    return null;
  }

  try {
    const profile = await liff.getProfile();
    return profile.userId;
  } catch {
    return null;
  }
}

/**
 * Check if running in LINE app
 */
export function isInLineApp(): boolean {
  return isInitialized && liff.isInClient();
}

/**
 * Close LIFF window (only works in LINE app)
 */
export function closeLiff(): void {
  if (isInitialized && liff.isInClient()) {
    liff.closeWindow();
  }
}

/**
 * Open external URL
 */
export function openExternalUrl(url: string): void {
  if (isInitialized) {
    liff.openWindow({ url, external: true });
  } else {
    window.open(url, '_blank');
  }
}
